import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription, ReplaySubject, defer } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { Participant, Conversation, Message } from '@mitel/cloudlink-sdk/chat';
import { getItemsFromCollection } from '@mitel/cloudlink-sdk/utils';
import { HttpResponse, Config } from '@mitel/cloudlink-sdk';

import { ChatAuthService } from '../services/chat-auth.service';
import { ChatCloudLinkService } from '../services/chat-cloudlink.service';
import { ContentTypes } from '../classes/constants';
import { SharedHelpers } from '../classes/sharedhelpers';
import { ChatLoggingService, ChatLogEvent } from './chat-logging.service';
import { filter, retryWhen } from 'rxjs/operators';
import { ChatDatabaseService } from './chat-database.service';
import { MessageEx } from '../classes/messageex';
import { ChatAdminService } from './chat-admin.service';
import { UserConversation, MessagePutRequest } from '@mitel/cloudlink-sdk/chat';
import uniqBy from 'lodash-es/uniqBy';

declare const Paho: any;

export class ChatMessageErrorEvent {
  exceptionLogEvent: ChatLogEvent;
  errorMessage: string;
}

export class ConversationParticipants {
  conversationId: string;
  largeLoad?: boolean;
  total?: number;
  participants?: Participant[];
}

export class DatabaseConversation {
  conversationId: string;
  messageIds?: string[];
  lastMessage?: {
    body: string;
    contentType: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ChatConversationService {
  chatUrl: string;

  pendingAttachments: {
    [attachmentId: string]: { attachmentName: string; attachmentGroup: string };
  } = {};
  pendingAttachmentMessages: { [attachmentGroup: string]: MessageEx[] } = {};
  attachmentUploadAck$ = new Subject<{ conversationId: string; id: string }>();
  attachmentUploadSuccess$ = new Subject<{
    conversationId: string;
    id: string;
    name: string;
  }>();
  attachmentUploadFail$ = new Subject<{
    conversationId: string;
    id: string;
    name: string;
  }>();
  attachmentManualNotify$ = new Subject<any>();
  databaseListenForNewStarted = false;
  databaseNewTimeouts = {};
  databaseConversationListMax = 15;
  pageSize = 40;

  private attachmentCacheMaxBytes = 10 * 1024 * 1024;
  private attachmentCache = new Map<string, { promise: Promise<Blob>; size: number }>();
  private attachmentCacheBytes = 0;

  private conversationDeletedSource$ = new Subject<string>();
  private handlingPendingSends = false;
  private pendingSendTimeout: any;
  private handleConversationListAttempt = 0;

  conversationDeleted$ = this.conversationDeletedSource$.asObservable();

  participantsUpdated$: ReplaySubject<ConversationParticipants> = new ReplaySubject<ConversationParticipants>(1);
  chatInfoSetupContent$ = new Subject<void>();
  mentionClicked$ = new Subject<string>();
  errorDialog$ = new Subject<ChatMessageErrorEvent>();
  databaseConversationsResynced$ = new Subject<DatabaseConversation[]>();

  constructor(
    private chatAuthService: ChatAuthService,
    private chatCloudLinkService: ChatCloudLinkService,
    private http: HttpClient,
    private chatLoggingService: ChatLoggingService,
    private chatDatabaseService: ChatDatabaseService,
    private chatAdminService: ChatAdminService
  ) {
    var authSub = new Subscription();
    authSub.add(
      this.chatAuthService.ready$.subscribe(
        () => {
          authSub.unsubscribe();
          this.initialize();
        },
        er => {
          //Let chat component handle error
        }
      )
    );

    this.conversationDeleted$.subscribe(conversationId => this.clearAttachmentCache(conversationId));
  }

  initialize() {
    if (this.chatCloudLinkService.jsonMessagesAsSource || this.chatCloudLinkService.aiMode) {
      this.chatLoggingService.info({
        logMessage: 'chat-conversation.service.initialize() jsonMessagesAsSource/aiMode enabled - skipping'
      });
      return;
    }

    var cloudPrefix = '';
    var existingConfig = SharedHelpers.getSdkConfig();
    cloudPrefix = existingConfig.cloud !== 'public' ? '.' + existingConfig.cloud : '';

    this.chatUrl = `https://chat${cloudPrefix}.api.mitel.io/2017-09-01`;

    this.handleAttachments();

    if (!this.chatCloudLinkService.isOffline()) {
      this.handlePendingSends();
    }

    this.chatCloudLinkService.notify
      .asDiagnosticsObservable()
      .pipe(filter(event => event?.type === 'Connectivity' || event?.type === 'Websocket'))
      .subscribe(async message => {
        if (message?.category === 'MqttSocket' && message?.data?.type === 'disconnected') {
          this.chatLoggingService.info({
            logMessage: 'chat-conversation.service.initialize(): WebSocket disconnected'
          });
          for (let key of Object.keys(this.chatDatabaseService.synced)) {
            this.chatDatabaseService.synced[key] = false;
          }
        }
      });

    this.chatCloudLinkService.online$.subscribe(online => {
      if (online) {
        if (this.pendingSendTimeout) {
          clearTimeout(this.pendingSendTimeout);
        }
        let delay = 0;
        if (this.chatAuthService.offlineLogin) {
          //Wait for service to reset
          delay += 2500;
        }
        this.pendingSendTimeout = setTimeout(() => {
          let authSub = new Subscription();
          authSub.add(
            this.chatAuthService.ready$.subscribe(
              () => {
                authSub.unsubscribe();
                this.handlePendingSends();
              },
              er => {
                //Let chat component handle error
              }
            )
          );
        }, delay);
      } else {
        for (let key of Object.keys(this.chatDatabaseService.synced)) {
          this.chatDatabaseService.synced[key] = false;
        }
      }
    });
  }

  public listenForNewConversationForDatabase() {
    if (!this.databaseListenForNewStarted) {
      this.databaseListenForNewStarted = true;
      this.chatCloudLinkService.messageNotAddedToDb$.subscribe(async (message: Message) => {
        if (this.databaseNewTimeouts[message.conversationId]) {
          clearTimeout(this.databaseNewTimeouts[message.conversationId]);
        }
        //Need the delay to allow elasticDB to settle
        this.databaseNewTimeouts[message.conversationId] = setTimeout(async () => {
          delete this.databaseNewTimeouts[message.conversationId];
          try {
            await this.addNewConversationToDatabase(message.conversationId);
          } catch (ex) {
            this.chatLoggingService.error({
              logMessage:
                'chat-conversation.service.listenForNewConversationForDatabase() Error adding conversation to db',
              logParameters: [message.conversationId, ex]
            });
          }
        }, 3000);
      });
    }
  }

  public handleUserConversationListDb(userConversations: UserConversation[]) {
    let convs = userConversations.map(u => u.conversation as Conversation);
    this.handleConversationListDb(convs);
  }

  private getCreatedOnTime(conversation: Conversation) {
    if (conversation?._embedded?.messages?._embedded?.items?.length) {
      return new Date(conversation._embedded.messages._embedded.items[0].createdOn).getTime();
    }
    return new Date(conversation.createdOn).getTime();
  }

  public async handleConversationListDb(conversations: Conversation[]) {
    let attempt = this.handleConversationListAttempt;
    this.handleConversationListAttempt++;
    //Wait for auth service to be ready for the account id
    let authSub = new Subscription();
    authSub.add(
      this.chatAuthService.ready$.subscribe(async () => {
        authSub.unsubscribe();
        let sortedConvs = conversations.sort((a, b) => this.getCreatedOnTime(b) - this.getCreatedOnTime(a));
        let conversationsToTake: Conversation[] = [];
        let limit = this.databaseConversationListMax;
        sortedConvs.forEach(s => {
          if (limit !== 0) {
            if (s._embedded?.messages?._embedded?.items?.length) {
              limit--;
            }
            conversationsToTake.push(s);
          }
        });
        // //Process oldest to newest
        conversationsToTake = conversationsToTake.reverse();
        this.chatLoggingService.info({
          logMessage: 'chat-conversation.service.handleConversationListDb() Start',
          logParameters: [attempt]
        });
        let databaseConversations = [];
        for (let c of conversationsToTake) {
          if (!this.chatCloudLinkService.isOffline()) {
            this.chatLoggingService.debug({
              logMessage: 'chat-conversation.service.handleConversationListDb() Checking conversation',
              logParameters: [c.conversationId, attempt]
            });
            let added = await this.addConversationToDatabase(c);
            if (added) {
              let dbConv: DatabaseConversation = {
                conversationId: c.conversationId
              };
              let messages = await this.chatDatabaseService.getMessages(c.conversationId, c.accountId);
              if (messages?.length) {
                dbConv.messageIds = messages.map(m => m.messageId);
                dbConv.lastMessage = {
                  body: messages[0].body,
                  contentType: messages[0].contentType
                };
              }
              databaseConversations.push(dbConv);
            }
          }
        }
        if (databaseConversations?.length) {
          this.databaseConversationsResynced$.next(databaseConversations.reverse());
        }
        this.chatLoggingService.info({
          logMessage: 'chat-conversation.service.handleConversationListDb() Completed',
          logParameters: [attempt]
        });
      })
    );
  }

  public async addConversationToDatabase(conversation: Conversation): Promise<boolean> {
    try {
      let matchedConversation = await this.chatDatabaseService.getConversationFromDb(
        conversation.conversationId,
        this.chatAuthService.accountId
      );
      if (!matchedConversation) {
        await this.addNewConversationToDatabase(
          conversation.conversationId,
          !!conversation._embedded?.messages?._embedded?.items?.length
        );
        return true;
      } else if (conversation._embedded?.messages?._embedded?.items?.length) {
        let messages = await this.chatDatabaseService.getMessages(
          conversation.conversationId,
          this.chatAuthService.accountId
        );
        if (!messages.length) {
          let resynced = await this.resyncConversation(conversation.conversationId);
          return resynced;
        } else {
          if (messages[0].messageId !== conversation._embedded.messages._embedded.items[0].messageId) {
            let resynced = await this.resyncConversation(conversation.conversationId);
            return resynced;
          }
        }
      }
      return false;
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat-conversation.service.addConversationToDatabase() Error adding conversation to db',
        logParameters: [conversation.conversationId, ex]
      });
      return false;
    }
  }

  private async addNewConversationToDatabase(conversationId, getMessages = true) {
    if (!this.chatDatabaseService.synced[conversationId]) {
      let messageCollection = null;
      if (getMessages) {
        const resMessageCollection = await this.chatCloudLinkService.chat.getMessages({
          conversationId: conversationId,
          skip: 0,
          top: this.pageSize
        });

        messageCollection = resMessageCollection.body;
      }
      if (!this.chatDatabaseService.synced[conversationId]) {
        let messages = [];
        if (messageCollection) {
          messages = getItemsFromCollection(messageCollection);
          await this.checkUsers(messages);
        }
        await this.chatDatabaseService.addConversationToDb(conversationId, this.chatAuthService.accountId, messages);
        this.chatLoggingService.debug({
          logMessage: 'chat-conversation.service.addNewConversationToDatabase() Added new conversation to db',
          logParameters: [conversationId]
        });
      }
    }
  }

  private async resyncConversation(conversationId): Promise<boolean> {
    try {
      if (!this.chatDatabaseService.synced[conversationId]) {
        const resMessageCollection = await this.chatCloudLinkService.chat.getMessages({
          conversationId: conversationId,
          skip: 0,
          top: this.pageSize
        });
        let messageCollection = resMessageCollection.body;
        let messages = getItemsFromCollection(messageCollection);
        await this.checkUsers(messages);
        await this.chatDatabaseService.resyncConversation(conversationId, this.chatAuthService.accountId, messages);
        return true;
      }
      return false;
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'chat-conversation.service.resyncConversation() Error resyncing',
        logParameters: [conversationId, ex]
      });
      return false;
    }
  }

  private async checkUsers(messages: Message[]) {
    let messageUserIds = messages.map(m => m.createdBy);
    messageUserIds = uniqBy(messageUserIds, v => v);
    let dbUsers = await this.chatDatabaseService.getUsers();
    if (dbUsers) {
      messageUserIds = messageUserIds.filter(m => !dbUsers.find(d => d.principalId === m));
    }
    if (messageUserIds.length) {
      await this.chatAdminService.getPrincipals(messageUserIds, true);
    }
  }

  private async handlePendingSends() {
    if (!this.handlingPendingSends) {
      this.handlingPendingSends = true;
      try {
        let pendingSends = await this.chatDatabaseService.getPendingSendMessages();
        if (pendingSends) {
          this.chatLoggingService.info({
            logMessage: 'chat-conversation.service.handlePendingSends() Total pending sends',
            logParameters: [pendingSends.length]
          });
          for (let msg of pendingSends) {
            try {
              await this.chatDatabaseService.deleteMessage(msg.accountId, msg.messageId);
              this.chatCloudLinkService.sendMessagesDeleted(msg.messageId);
              try {
                const resSent = await this.chatCloudLinkService.chat.createMessage({
                  conversationId: msg.conversationId,
                  body: {
                    body: msg.body,
                    contentType: msg.contentType,
                    fallbackText: msg.fallbackText
                  }
                });
                let sent = resSent.body;
                this.chatLoggingService.info({
                  logMessage: 'chat-conversation.service.handlePendingSends() Sent pending send',
                  logParameters: [sent.messageId, msg.conversationId]
                });
              } catch (ex) {
                this.chatLoggingService.error({
                  logMessage: 'chat-conversation.service.handlePendingSends() Error sending pending send',
                  logParameters: [msg.messageId, msg.conversationId, ex]
                });
                try {
                  msg.state = 'failed';
                  msg.createdOn = <any>new Date().toISOString();
                  msg.modifiedOn = <any>new Date().toISOString();
                  await this.chatDatabaseService.addMessageIfConversationExists(
                    this.chatDatabaseService.convertDbToMessage(msg)
                  );
                  this.chatCloudLinkService.sendMessagesChanged(this.chatDatabaseService.convertDbToMessage(msg, true));
                } catch (ex) {
                  this.chatLoggingService.error({
                    logMessage: 'chat-conversation.service.handlePendingSends() Error creating failed send',
                    logParameters: [msg.messageId, msg.conversationId, ex]
                  });
                }
              }
            } catch (ex) {
              this.chatLoggingService.error({
                logMessage: 'chat-conversation.service.handlePendingSends() Error deleting pending send',
                logParameters: [msg.messageId, msg.conversationId, ex]
              });
              try {
                msg.state = 'failed';
                await this.chatDatabaseService.updateMessage(this.chatDatabaseService.convertDbToMessage(msg));
                this.chatCloudLinkService.sendMessagesChanged(this.chatDatabaseService.convertDbToMessage(msg));
              } catch (ex) {
                this.chatLoggingService.error({
                  logMessage: 'chat-conversation.service.handlePendingSends() Error failing pending send',
                  logParameters: [msg.messageId, msg.conversationId, ex]
                });
              }
            }
          }
        }
      } catch (ex) {
        this.chatLoggingService.error({
          logMessage: 'chat-conversation.service.handlePendingSends() Error getting pending sends',
          logParameters: [ex]
        });
      }
      this.handlingPendingSends = false;
    }
  }

  handleAttachments() {
    this.chatCloudLinkService.attachmentsChanged$
      .pipe(filter(attachment => attachment.createdBy === this.chatAuthService.userId))
      .subscribe(async resource => {
        this.chatLoggingService.info({
          logMessage: 'chat-conversation.service.handleAttachments()',
          logParameters: [resource.name, (<any>resource).conversationId, resource.displayName]
        });
        if (this.pendingAttachments[resource.name] && !resource.displayName) {
          let attachmentName = this.pendingAttachments[resource.name].attachmentName;
          let attachmentGroup = this.pendingAttachments[resource.name].attachmentGroup;
          this.attachmentUploadAck$.next({
            conversationId: (<any>resource).conversationId,
            id: resource.name
          });
          this.chatLoggingService.info({
            logMessage: 'chat-conversation.service.handleAttachments(): Handling updating attachment meta ',
            logParameters: [resource.name, attachmentName, attachmentGroup]
          });
          try {
            await defer(() =>
              this.chatCloudLinkService.chat.updateAttachmentMeta({
                conversationId: (<any>resource).conversationId,
                attachmentId: resource.name,
                body: { displayName: attachmentName }
              })
            )
              .pipe(
                retryWhen(
                  SharedHelpers.genericRetryStrategy(
                    '[chat.component.handleAttachments()] - updateAttachmentMeta ' +
                      (<any>resource).conversationId +
                      ' ' +
                      resource.name,
                    this.chatLoggingService
                  )
                )
              )
              .toPromise();
            let foundPendingAttachmentMessageMatch = !!this.pendingAttachmentMessages[attachmentGroup];
            delete this.pendingAttachments[resource.name];
            this.chatLoggingService.info({
              logMessage: 'chat-conversation.service.handleAttachments(): Updated attachment meta'
            });
            if (!foundPendingAttachmentMessageMatch) {
              this.attachmentUploadSuccess$.next({
                conversationId: (<any>resource).conversationId,
                id: resource.name,
                name: attachmentName
              });
            } else {
              let sendMessages = true;
              Object.keys(this.pendingAttachments).forEach(k => {
                if (this.pendingAttachments[k].attachmentGroup === attachmentGroup) {
                  sendMessages = false;
                }
              });
              if (sendMessages) {
                let attachmentMessages = this.pendingAttachmentMessages[attachmentGroup].slice();
                delete this.pendingAttachmentMessages[attachmentGroup];
                for (let message of attachmentMessages) {
                  try {
                    await defer(() =>
                      this.chatCloudLinkService.chat.createMessage({
                        conversationId: message.conversationId,
                        body: {
                          body: message.body,
                          contentType: message.contentType,
                          fallbackText: message.fallbackText,
                          tag: message.tag
                        }
                      })
                    )
                      .pipe(
                        retryWhen(
                          SharedHelpers.genericRetryStrategy(
                            '[chat.component.handleAttachments()] - createMessage ' +
                              message.conversationId +
                              ' ' +
                              message.body,
                            this.chatLoggingService
                          )
                        )
                      )
                      .toPromise();
                    this.attachmentUploadSuccess$.next({
                      conversationId: message.conversationId,
                      id: message.body,
                      name: message.fallbackText
                    });
                  } catch (ex) {
                    this.chatLoggingService.error({
                      logMessage: 'chat-conversation.service.handleAttachments(): Error creating message',
                      logParameters: [ex]
                    });
                    this.attachmentUploadFail$.next({
                      conversationId: message.conversationId,
                      id: message.body,
                      name: message.fallbackText
                    });
                  }
                }
              }
            }
          } catch (ex) {
            delete this.pendingAttachments[resource.name];
            this.pendingAttachmentMessages[attachmentGroup].splice(
              this.pendingAttachmentMessages[attachmentGroup].findIndex(p => p.body === resource.name),
              1
            );
            if (this.pendingAttachmentMessages[attachmentGroup].length === 0) {
              delete this.pendingAttachmentMessages[attachmentGroup];
            }
            this.chatLoggingService.error({
              logMessage: 'chat-conversation.service.handleAttachments(): Error meta',
              logParameters: [ex]
            });
            this.attachmentUploadFail$.next({
              conversationId: (<any>resource).conversationId,
              id: resource.name,
              name: attachmentName
            });
          }
        }
      });
  }

  leaveStream(conversationId: string): Promise<void> {
    return this.chatCloudLinkService.chat
      .deleteParticipant({
        conversationId: conversationId,
        participantId: this.chatAuthService.userId
      })
      .then(result => {
        this.conversationDeletedSource$.next(conversationId);
        return;
      });
  }

  deleteStream(conversationId: string): Promise<void> {
    // CLCC-564 to support system messages
    return this.chatCloudLinkService.chat
      .createMessage({
        conversationId: conversationId,
        body: {
          body: 'Chat has ended.',
          contentType: ContentTypes.HTML
        }
      })
      .then(message => {
        this.chatCloudLinkService.chat.deleteConversation({ conversationId: conversationId }).then(() => {
          this.conversationDeletedSource$.next(conversationId);
        });
      });
  }

  async downloadAttachment(params: { conversationId: string; attachmentId: string; name?: string }) {
    let windowNavigator: any = window.navigator;
    try {
      var response = await this.getAttachmentAsBlob({
        conversationId: params.conversationId,
        attachmentId: params.attachmentId
      });
      if (this.chatCloudLinkService.browser.os.toLowerCase() === 'ios') {
        var reader = new FileReader();
        reader.onload = e => {
          var a = document.createElement('a');
          (<any>a.href) = reader.result;
          document.body.appendChild(a);
          a.download = params.name || params.attachmentId;
          a.click();
          if (a.parentNode) {
            a.parentNode.removeChild(a);
          }
        };
        reader.readAsDataURL(response);
      } else if (windowNavigator && windowNavigator.msSaveOrOpenBlob) {
        windowNavigator.msSaveOrOpenBlob(response, params.name || params.attachmentId);
      } else {
        let url: string = window.URL.createObjectURL(response);
        var a = document.createElement('a');
        (<any>a).style = 'display: none';
        a.href = url;
        document.body.appendChild(a);
        a.download = params.name || params.attachmentId;
        a.click();
        window.URL.revokeObjectURL(url);
        if (a.parentNode) {
          a.parentNode.removeChild(a);
        }
      }
    } catch (error) {
      throw error;
    }
  }

  async updateLastRead(conversationId: string, lastReadMessageDate: Date) {
    await this.chatCloudLinkService.chat.updateParticipant({
      conversationId: conversationId,
      participantId: this.chatAuthService.userId,
      body: { lastReadMessageDate: lastReadMessageDate }
    });
  }

  getConversation(conversationId: string): Promise<HttpResponse<Conversation>> {
    return this.chatCloudLinkService.chat.getConversation({
      conversationId: conversationId
    });
  }

  acceptInvite(conversationId: string): Promise<HttpResponse<Participant>> {
    return this.chatCloudLinkService.chat.updateParticipant({
      conversationId: conversationId,
      participantId: this.chatAuthService.userId,
      body: {
        status: 'ACCEPTED'
      }
    });
  }

  joinConversation(conversationId: string): Promise<void | Conversation> {
    //Check if already participant
    return this.chatCloudLinkService.chat
      .getParticipant({
        conversationId: conversationId,
        participantId: this.chatAuthService.userId
      })
      .then(res => {
        this.chatLoggingService.info({
          logMessage: 'chat-conversation.service.joinConversation()=>already a member',
          logParameters: [conversationId]
        });
      })
      .catch(() => {
        //Not yet a member, try to add them.
        return this.chatCloudLinkService.chat
          .createParticipant({
            conversationId: conversationId,
            body: {
              userId: this.chatAuthService.userId,
              status: 'ACCEPTED'
            }
          })
          .catch(err => {
            return Promise.reject(err);
          })
          .then(response => {
            return this.chatCloudLinkService.chat.updateParticipant({
              conversationId: conversationId,
              participantId: this.chatAuthService.userId,
              body: { status: 'ACCEPTED' }
            });
          })
          .then(response => {
            this.chatLoggingService.info({
              logMessage: 'chat-conversation.service.joinConversation()=>done',
              logParameters: [conversationId]
            });
          })
          .catch(ex => {
            this.chatLoggingService.error({
              logMessage: 'chat-conversation.service.joinConversation()=>error',
              logParameters: [ex]
            });
          });
      });
  }

  getAttachmentAsBlob(params: { conversationId: string; attachmentId: string }): Promise<Blob> {
    const key = `${params.conversationId}:${params.attachmentId}`;
    const cached = this.attachmentCache.get(key);
    if (cached) {
      //Refresh recency: Map preserves insertion order, so delete+re-set moves this entry to the end for LRU eviction
      this.attachmentCache.delete(key);
      this.attachmentCache.set(key, cached);
      return cached.promise;
    }

    const promise = this.fetchAttachmentAsBlob(params)
      .then(blob => {
        const entry = this.attachmentCache.get(key);
        if (entry) {
          this.attachmentCacheBytes += blob.size;
          entry.size = blob.size;
          this.evictAttachmentCacheOverflow(key);
        }
        return blob;
      })
      .catch(ex => {
        //Don't let a transient failure be cached forever - next call retries the request
        this.attachmentCache.delete(key);
        throw ex;
      });

    this.attachmentCache.set(key, { promise, size: 0 });
    return promise;
  }

  private clearAttachmentCache(conversationId: string) {
    const prefix = `${conversationId}:`;
    for (const key of Array.from(this.attachmentCache.keys())) {
      if (key.startsWith(prefix)) {
        this.attachmentCacheBytes -= this.attachmentCache.get(key)?.size ?? 0;
        this.attachmentCache.delete(key);
      }
    }
  }

  private evictAttachmentCacheOverflow(keepKey: string) {
    for (const oldestKey of this.attachmentCache.keys()) {
      if (this.attachmentCacheBytes <= this.attachmentCacheMaxBytes || this.attachmentCache.size <= 1) {
        break;
      }
      if (oldestKey === keepKey) {
        continue;
      }
      this.attachmentCacheBytes -= this.attachmentCache.get(oldestKey)?.size ?? 0;
      this.attachmentCache.delete(oldestKey);
    }
  }

  private fetchAttachmentAsBlob(params: { conversationId: string; attachmentId: string }): Promise<Blob> {
    return this.chatCloudLinkService.chat
      .getAttachmentBinary(params)
      .then(res => {
        const response = res.body;
        if (response.type.includes('text') && typeof response.stream === 'string') {
          return {
            type: response.type,
            data: response.stream
          };
        } else {
          const byteArrays: any[] = [];
          const reader = response.stream.getReader();
          return reader.read().then(data => {
            var process = (data: any): Promise<{ type: string; data: Uint8Array }> => {
              if (data.value) {
                byteArrays.push(data.value);
              }
              if (data.done) {
                return Promise.resolve({
                  type: response.type,
                  data: this.concatByteArrays(byteArrays)
                });
              } else {
                return reader.read().then(process);
              }
            };
            return process(data);
          });
        }
      })
      .then(result => {
        var type = result.type || 'application/octet-stream';
        //Need jpeg type set to check for EXIF data later on
        if (
          params.attachmentId.endsWith('.jpg') ||
          params.attachmentId.endsWith('.jpeg') ||
          params.attachmentId.endsWith('.jfif')
        ) {
          type = 'image/jpeg';
        }

        if (type.includes('text') && typeof result.data === 'string') {
          return new Blob([result.data], { type: type });
        } else {
          return new Blob([new Uint8Array(result.data)], { type: type });
        }
      })
      .catch(ex => {
        this.chatLoggingService.error({
          logMessage: 'chat-conversation.service.fetchAttachmentAsBlob() error',
          logParameters: [ex]
        });
        throw ex;
      });
  }

  private getAttachment(url: string, attachmentId: string): Observable<any> {
    let responseType: any = attachmentId.endsWith('.pdf') ? 'arraybuffer' : 'blob';

    return this.http.get(url, {
      headers: {
        Authorization: this.chatAuthService.token.access_token,
        'Content-Type': 'application/octet-stream',
        Accept: '*/*'
      },
      responseType: responseType
    });
  }

  private concatByteArrays(data: Uint8Array[]): Uint8Array {
    let len = 0;
    for (let datum of data) {
      len += datum.length;
    }

    const result = new Uint8Array(len);
    let offset = 0;
    for (let datum of data) {
      result.set(datum, offset);
      offset += datum.length;
    }

    return result;
  }

  async deleteMessage(params: { conversationId: string; messageId: string }) {
    await this.chatCloudLinkService.chat.deleteMessage({
      conversationId: params.conversationId,
      messageId: params.messageId
    });
  }

  async deleteAttachment(params: { conversationId: string; attachmentId: string }) {
    await this.chatCloudLinkService.chat.deleteAttachment({
      conversationId: params.conversationId,
      attachmentId: params.attachmentId
    });
  }

  async editMessage(params: { conversationId: string; messageId: string; body: MessagePutRequest }) {
    await this.chatCloudLinkService.chat.updateMessage({
      conversationId: params.conversationId,
      messageId: params.messageId,
      body: params.body
    });
  }
}
